import React, { useEffect } from 'react';
import type { BuildoutListing, AnywhereListing } from '../../types';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { setListingGuid } from '../../api';
import confirm from 'components/confirm';
import { buildoutTypeLabel } from '../../helpers';
import classNames from 'classnames';
import { titleize } from 'helpers/formatters/string_formats';
import { useDrop } from 'react-dnd';

type BuildoutListingRowProps = {
  listing: BuildoutListing;
  updateListingGuid: (guid: string, buildoutListingId: number) => void;
  setFlash: (message?: string | null, variant?: string) => void;
}

function BuildoutListingRow({ listing, setFlash, updateListingGuid }: BuildoutListingRowProps) {
  const [guid, setGuid] = React.useState<string>(listing.guid || '');
  const [guidSaved, setGuidSaved] = React.useState<boolean>(listing.guid ? true : false);
  const [hovering, setHovering] = React.useState<boolean>(false);

  // Function duplicated in matched_listings_table.tsx
  const handleRemoveGuid = () => {
    confirm(`Are you sure you want to remove the GUID ${listing.guid} from listing ID ${listing.id}?`, {
      buttonLabel: 'Delete',
      buttonStyle: 'danger',
      title: `Delete Date?`
    }).then(() => {
      setListingGuid(listing.id, null).then(() => {
        updateListingGuid('', listing.id);
        setGuid('');
        setFlash('GUID removed!', 'warning');
        setGuidSaved(false);
      });
    });
  };

  const saveListingGuid = () => {
    setListingGuid(listing.id, guid).then(() => {
      updateListingGuid(guid, listing.id);
      if (guid) setFlash('GUID saved successfully!', 'success');
      setGuidSaved(true);
    });
  };

  const ref = React.useRef(null);
  const [{ isOver }, drop] = useDrop({
    accept: 'listing-guid',
    drop(item: AnywhereListing) {
      if (item.listingGuid) {
        setGuid(item.listingGuid);
        setListingGuid(listing.id, item.listingGuid).then(() => {
          updateListingGuid(item.listingGuid, listing.id);
          setFlash('GUID updated successfully!', 'success');
        });
      } else {
        setFlash('No GUID found for this listing.', 'warning');
      }
    },
    hover(item) {
      if (item.listingGuid) {
        setGuid(item.listingGuid);
        setGuidSaved(false);
      }
    },
    collect: monitor => ({
      isOver: !!monitor.isOver(),
    }),
  });

  useEffect(() => {
    if (isOver && !hovering) {
      setHovering(true);
    } else if (!isOver && hovering) {
      setHovering(false);
      setGuid(listing.guid || '');
      setGuidSaved(listing.guid ? true : false);
    }
  }, [isOver, hovering]);

  drop(ref);

  const renderGuidInput = () => {
    return (
      <td>
        <input
          className="form-control"
          placeholder="Enter GUID"
          type="text"
          value={guid}
          onChange={e => setGuid(e.target.value)}
        />
        <button
          className="btn btn-primary mt-2"
          onClick={saveListingGuid}
        >
          Save GUID
        </button>
      </td>
    );
  };

  const guidCol = React.useMemo(() => {
    if (!guidSaved) return renderGuidInput();

    const tooltip = (
      <Tooltip id={`tooltip-${listing.guid}`}>
        <span>This GUID does not match any AnywhereRE listings. Click to remove.</span>
      </Tooltip>
    );

    return (
      <td className="bg-danger clickable" onClick={handleRemoveGuid}>
        <OverlayTrigger overlay={tooltip} placement="left">
          <div>
            <div>
              {listing.guid}
            </div>
            <div className="text-muted small">
              Click to remove
            </div>
          </div>
        </OverlayTrigger>
      </td>
    );
  }, [guid, guidSaved, listing]);

  const statusCol = React.useMemo(() => {
    const { status } = listing;
    if (!status) return <td className="text-muted">N/A</td>;

    return <td className={classNames({ 'bg-warning': (status.toLowerCase() === 'proposal') })}>{titleize(status)}</td>;
  }, [listing]);

  const titleLink = React.useMemo(() => {
    const tooltip = (
      <Tooltip id={`tooltip-${listing.propertyId}`}>
        <span>View Property</span>
      </Tooltip>
    );

    return (
      <td>
        <OverlayTrigger overlay={tooltip} placement="top">
          <a className="btn-link" href={`/properties/${listing.propertyId}`} rel="noopener noreferrer" target="_blank">
            {listing.name}
          </a>
        </OverlayTrigger>
      </td>
    );
  }, [listing]);

  return (
    <tr className={classNames({ 'bg-success': hovering })} key={listing.id} ref={ref}>
      <td>
        <div>{buildoutTypeLabel(listing.class)}</div>
        <div className="text-muted small text-nowrap">Listing #{listing.id}</div>
        <div className="text-muted small text-nowrap">Property #{listing.propertyId}</div>
      </td>
      {titleLink}
      <td>{listing.address}, {listing.city}, {listing.state} {listing.zip}</td>
      {statusCol}
      {guidCol}
    </tr>
  );
}

export default BuildoutListingRow;
